import React, { useState } from 'react'
import { connect } from 'react-redux'
import EmailVerificationEmailPasswordForm from '../../../components/modals/emailVerification/EmailVerificationEmailPasswordForm'
import EmailVerificationCodeEntry from '../../../components/modals/emailVerification/EmailVerificationCodeEntry'
import ReauthenticationModal from '../../../components/reauthenticationModal/ReauthenticationModal'
import {
  createEmailVerification, resendEmailVerification, verifyEmailVerification,
} from '../../../actions/emailVerification'
import { showNotification } from '../../../actions/notifications'
import useEmailVerificationRequest from '../../../utils/emailVerification/useEmailVerificationRequest'

// Simple, standalone "update account email" flow for the account page — just
// email (+ password reauth if the session needs it) -> code -> done. This is
// intentionally separate from the bigger email-verification prompt modal.
function UpdateEmailContainer(props) {
  const {
    user, hideModal, createEmailVerification, resendEmailVerification, verifyEmailVerification,
    showNotification,
  } = props

  const [step, setStep] = useState('form') // 'form' | 'code'
  const {
    verification, requestCode, requestLoading, requestError,
    showReauth, handleReauthenticate, cancelReauth, submitCode, resend,
  } = useEmailVerificationRequest({
    userId: user.id,
    createEmailVerification,
    resendEmailVerification,
    verifyEmailVerification,
    onCodeSent: () => setStep('code'),
  })

  const handleSubmit = ({ email }) => requestCode({ purpose: 'updatePrimary', email })

  const handleVerifyCode = async (code) => {
    await submitCode(code)
    hideModal()
    showNotification('', 'Account email address updated', 'default')
  }

  if (step === 'code') {
    return (
      <EmailVerificationCodeEntry
        email={verification.email}
        title='Verify your new email address'
        description="We've sent a code to your new email address. Please enter the code to finish updating your Plickers email"
        resendAvailableAt={verification.resendAvailableAt}
        showIllustration={false}
        onSubmitCode={handleVerifyCode}
        onResend={resend}
      />
    )
  }

  return (
    <React.Fragment>
      <EmailVerificationEmailPasswordForm
        title='Update account email address'
        description="We'll send a code to your new email address to complete the change."
        showIllustration={false}
        emailLabel='New email address'
        submitLabel='Send code'
        currentEmail={user.email}
        backupEmail={user.backupEmail}
        duplicateEmailError='Please enter a different email address than your account email address.'
        submitLoading={requestLoading}
        errorMessage={requestError}
        onSubmit={handleSubmit}
      />
      {showReauth && (
        <ReauthenticationModal
          title='Update Account Email Address'
          // updatePrimary only accepts a password — a google/apple-only
          // account can't change its primary email through this flow
          signinMethodsToDisplay={{ password: true }}
          onAuthenticate={handleReauthenticate}
          hideModal={cancelReauth}
        />
      )}
    </React.Fragment>
  )
}

export default connect(
  (state) => ({ user: state.user }),
  {
    createEmailVerification, resendEmailVerification, verifyEmailVerification, showNotification,
  },
)(UpdateEmailContainer)
